<template>
  <div class="qa-widget-frame">
    <iframe
      ref="frame"
      title="qa-widget"
      :src="widgetUrl"
      width="100%"
      height="100%"
      allowfullscreen
      allow="fullscreen"
      style="border: none;" />
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onBeforeUnmount,
} from 'vue';
import config from '@/config'; // eslint-disable-line import/extensions
import { getWidgetRoute } from '@/router/helpers';
import { toggleQaFullscreen } from './qaFullscreen';
import {
  QA_INIT, QA_DRAW_MODE, QA_GOTO_ISSUE, QA_HOVER_ISSUE, QA_ISSUES_SYNC, QA_CAPTURE_AFTER,
  CONTENT_READY, QA_ISSUE_DRAFTED, QA_ISSUE_RESOLVED, QA_PROGRESS, QA_CAMERA_CHANGED, QA_AFTER_CAPTURED,
  QA_VARIANTS, QA_ISSUE_CLONE_REQUESTED, QA_OPEN_SCREENSHOTS, QA_SET_STAGE, QA_STAGE_CHANGED,
  QA_ANNOTATOR_STATE, QA_FULLSCREEN, QA_GOTO_ROOM,
} from './qaProtocol';

const props = defineProps({
  crmPlanId: { type: [Number, String], required: true },
  mode: { type: String, required: true },
  issues: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'ready',
  'issue-drafted',
  'issue-clone-requested',
  'issue-resolved',
  'progress',
  'camera-changed',
  'variants',
  'open-screenshots',
  'stage-changed',
  'annotator-state',
]);

const frame = ref(null);

const widgetUrl = computed(() => getWidgetRoute({
  crmPlanId: props.crmPlanId,
  mode: props.mode,
  reviewMode: 1,
  noCache: 1,
  locale: 'ru',
  // Проверка начинается с этапа 2D — открываем виджет сразу на вкладке плана
  // (без переключения из плагина, которое ломает контролы).
  ...(props.mode === 'review' ? { primarytab: 'plan' } : {}),
  ...(config.widget.dataBaseUrl ? { baseurl: config.widget.dataBaseUrl } : {}),
}).toString());

const widgetOrigin = computed(() => new URL(config.widget.url).origin);

const cloneForPostMessage = (value) => {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
};

const post = (message) => {
  frame.value?.contentWindow?.postMessage(cloneForPostMessage(message), widgetOrigin.value);
};

const init = () => post({
  type: QA_INIT,
  mode: props.mode,
  issues: props.issues,
});

const setDrawMode = (on) => post({ type: QA_DRAW_MODE, on });
const gotoIssue = (issueId) => post({ type: QA_GOTO_ISSUE, issueId });
const hoverIssue = (issueId) => post({ type: QA_HOVER_ISSUE, issueId });
const syncIssues = (issues) => post({ type: QA_ISSUES_SYNC, issues });
const gotoRoom = ({ floorIndex, cameraPointId }) => post({ type: QA_GOTO_ROOM, floorIndex, cameraPointId });
const setStage = (stage) => post({ type: QA_SET_STAGE, stage });

const pendingCaptures = new Map();
const AFTER_CAPTURE_TIMEOUT = 7000;

const captureAfter = (issueId) => new Promise((resolve) => {
  const timer = setTimeout(() => {
    pendingCaptures.delete(issueId);
    resolve(null);
  }, AFTER_CAPTURE_TIMEOUT);
  pendingCaptures.set(issueId, { resolve, timer });
  post({ type: QA_CAPTURE_AFTER, issueId });
});

const onMessage = (event) => {
  if (event.origin !== widgetOrigin.value || event.source !== frame.value?.contentWindow) return;
  const data = event.data || {};

  switch (data.type) {
    case QA_FULLSCREEN:
      toggleQaFullscreen().catch(() => {});
      break;
    case CONTENT_READY:
      init();
      emit('ready');
      break;
    case QA_ISSUE_DRAFTED:
      emit('issue-drafted', { anchor: data.anchor, screenshot: data.screenshot, planId: data.planId });
      break;
    case QA_ISSUE_CLONE_REQUESTED:
      emit('issue-clone-requested', {
        issue: data.issue,
        anchor: data.anchor,
        screenshot: data.screenshot,
        planId: data.planId,
      });
      break;
    case QA_ISSUE_RESOLVED:
      emit('issue-resolved', {
        issueId: data.issueId,
        resolution: data.resolution,
        screenshot: data.screenshot,
      });
      break;
    case QA_PROGRESS:
      emit('progress', {
        mode: data.mode,
        done: data.done,
        total: data.total,
        canSubmit: data.canSubmit,
        rooms: data.rooms ?? [],
        currentFloorIndex: data.currentFloorIndex,
      });
      break;
    case QA_CAMERA_CHANGED:
      emit('camera-changed', { cameraPointId: data.cameraPointId, roomId: data.roomId });
      break;
    case QA_STAGE_CHANGED:
      emit('stage-changed', data.stage);
      break;
    case QA_ANNOTATOR_STATE:
      emit('annotator-state', Boolean(data.open));
      break;
    case QA_VARIANTS:
      emit('variants', { variantIds: data.variants ?? [], activeVariantId: data.activeVariantId ?? null });
      break;
    case QA_OPEN_SCREENSHOTS:
      emit('open-screenshots', {
        images: Array.isArray(data.images) ? data.images : [],
        index: Number(data.index ?? 0),
      });
      break;
    case QA_AFTER_CAPTURED: {
      const pending = pendingCaptures.get(data.issueId);
      if (pending) {
        clearTimeout(pending.timer);
        pendingCaptures.delete(data.issueId);
        pending.resolve(data.screenshot ?? null);
      }
      break;
    }
    default:
      break;
  }
};

onMounted(() => window.addEventListener('message', onMessage));
onBeforeUnmount(() => window.removeEventListener('message', onMessage));

defineExpose({
  init, setDrawMode, gotoIssue, hoverIssue, syncIssues, captureAfter, setStage, gotoRoom,
});
</script>

<style scoped>
.qa-widget-frame {
  width: 100%;
  height: 100%;
  min-height: 0;
}
.qa-widget-frame iframe {
  display: block;
}
</style>
